import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KB_AUDIENCES } from "@/lib/kbAudiences";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import AppUnavailable from "@/pages/AppUnavailable";

export default function KBBulkVisibility() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [options, setOptions] = useState({ teams: [], organizations: [] });
  const [q, setQ] = usePersistedState("kb-bulk-visibility.q", "");
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [orgFilter, setOrgFilter] = usePersistedState("kb-bulk-visibility.org", "");
  const [tree, setTree] = useState([]);
  const [cats, setCats] = useState([]);
  // apply panel
  const [applyAud, setApplyAud] = useState(false);
  const [applyTeams, setApplyTeams] = useState(false);
  const [applyOrgs, setApplyOrgs] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [teams, setTeams] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/kb/tree").then((r) => setTree(r.data || [])).catch(() => {});
    api.get("/kb/visibility/options")
      .then((r) => setOptions({ teams: r.data.teams || [], organizations: r.data.organizations || [] }))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kb/manage/articles", {
        params: { q, limit: 100, category_ids: cats.join(",") },
      });
      setList(data);
    } finally { setLoading(false); }
  }, [q, cats]);

  useEffect(() => { search(); }, [search]);

  const toggleSel = (slug) => setSelected((s) => {
    const n = new Set(s); n.has(slug) ? n.delete(slug) : n.add(slug); return n;
  });
  const selectAll = () => {
    if (!list) return;
    const all = list.articles.map((a) => a.slug);
    setSelected((s) => s.size === all.length ? new Set() : new Set(all));
  };
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const apply = async () => {
    if (selected.size === 0) { toast.error("Select at least one article"); return; }
    if (!applyAud && !applyTeams && !applyOrgs) { toast.error("Choose what to update"); return; }
    setSaving(true);
    try {
      const payload = { kind: "article", slugs: [...selected] };
      if (applyAud) payload.audiences = audiences;
      if (applyTeams) payload.target_teams = teams;
      if (applyOrgs) payload.target_orgs = orgs;
      const { data } = await api.post("/kb/visibility/bulk", payload);
      toast.success(`Updated ${data.updated} article(s)`);
      setSelected(new Set());
      search();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;
  const filteredOrgs = options.organizations.filter((o) => !orgFilter || (o.name || "").toLowerCase().includes(orgFilter.toLowerCase()));

  return (
    <div className="mx-auto max-w-5xl" data-testid="kb-bulk-visibility-page">
      <button onClick={() => navigate("/kb/visibility")} data-testid="kb-bulk-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> Back to Visibility
      </button>
      <h1 className="mb-4 font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Bulk Visibility</h1>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Article list */}
        <div className="lg:col-span-3">
          <div className="mb-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <Input data-testid="kb-bulk-search" value={q} placeholder="Filter articles by title…"
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") search(); }} className="pl-9" />
            </div>
            <div className="w-[190px]">
              <KbCategoryFilter tree={tree} value={cats} testid="kb-bulk-cat"
                onChange={setCats} />
            </div>
            <Button variant="outline" onClick={search} data-testid="kb-bulk-search-btn">Search</Button>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
              <button onClick={selectAll} data-testid="kb-bulk-select-all" className="font-medium text-primary hover:underline">
                {list && selected.size === list.articles.length && list.articles.length > 0 ? "Clear all" : "Select all"}
              </button>
              <span>{selected.size} selected{list ? ` · ${list.total} total` : ""}</span>
            </div>
            <div className="max-h-[520px] overflow-y-auto" data-testid="kb-bulk-list">
              {loading ? (
                <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
              ) : !list || list.articles.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">No articles.</div>
              ) : list.articles.map((a) => (
                <label key={a.slug} data-testid={`kb-bulk-row-${a.slug}`}
                  className="flex cursor-pointer items-center gap-3 border-b border-slate-50 dark:border-slate-800 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <input type="checkbox" data-testid={`kb-bulk-check-${a.slug}`}
                    checked={selected.has(a.slug)} onChange={() => toggleSel(a.slug)} className="h-4 w-4" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{a.name}</span>
                  {a.is_draft && <span className="rounded bg-amber-100 px-1.5 text-[10px] text-amber-700">Draft</span>}
                  {a.is_snippet && <span className="rounded bg-indigo-100 px-1.5 text-[10px] text-indigo-700">Snippet</span>}
                  <span className="hidden shrink-0 text-[10px] text-slate-400 dark:text-slate-500 sm:inline">{(a.audiences || []).join(", ") || "staff-only"}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Apply panel */}
        <div className="lg:col-span-2">
          <div className="sticky top-4 space-y-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid="kb-bulk-apply">
            <h2 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Apply to {selected.size} selected</h2>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input type="checkbox" data-testid="kb-bulk-apply-aud" checked={applyAud} onChange={(e) => setApplyAud(e.target.checked)} /> Set audiences
              </label>
              {applyAud && (
                <div className="flex flex-wrap gap-1.5">
                  {KB_AUDIENCES.map((a) => (
                    <button key={a.value} type="button" data-testid={`kb-bulk-aud-${a.value}`}
                      onClick={() => toggle(audiences, setAudiences, a.value)}
                      className={`rounded-full border px-2.5 py-1 text-xs ${audiences.includes(a.value) ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400"}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input type="checkbox" data-testid="kb-bulk-apply-teams" checked={applyTeams} onChange={(e) => setApplyTeams(e.target.checked)} /> Set teams
              </label>
              {applyTeams && (
                <div className="flex flex-wrap gap-1.5">
                  {options.teams.map((t) => (
                    <button key={t} type="button" data-testid={`kb-bulk-team-${t}`}
                      onClick={() => toggle(teams, setTeams, t)}
                      className={`rounded-full border px-2.5 py-1 text-xs ${teams.includes(t) ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400"}`}>
                      {t}
                    </button>
                  ))}
                  <p className="w-full text-[11px] text-slate-400 dark:text-slate-500">Empty = clears team targeting.</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input type="checkbox" data-testid="kb-bulk-apply-orgs" checked={applyOrgs} onChange={(e) => setApplyOrgs(e.target.checked)} /> Set organizations
              </label>
              {applyOrgs && (
                <div className="space-y-1.5">
                  <Input data-testid="kb-bulk-org-filter" value={orgFilter} placeholder="Filter organizations…"
                    onChange={(e) => setOrgFilter(e.target.value)} className="h-8 text-xs" />
                  <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-md border border-slate-100 dark:border-slate-800 p-1">
                    {filteredOrgs.slice(0, 80).map((o) => (
                      <button key={o.org_id} type="button" data-testid={`kb-bulk-org-${o.org_id}`}
                        onClick={() => toggle(orgs, setOrgs, o.org_id)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs ${orgs.includes(o.org_id) ? "bg-indigo-50 text-indigo-700" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                        <span className="truncate">{o.name}</span>{orgs.includes(o.org_id) && <span>✓</span>}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">Empty = clears org targeting.</p>
                </div>
              )}
            </div>

            <Button onClick={apply} disabled={saving || selected.size === 0} data-testid="kb-bulk-apply-btn"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Apply changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
